import React from 'react';
import { ArrowLeft, Clock3 } from 'lucide-react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';

function clampPercentage(value) {
	const num = Number(value);
	if (Number.isNaN(num)) return 0;
	return Math.max(0, Math.min(100, Math.round(num)));
}

function normalizeScorePercent(summary) {
	const scorePercentage = Number(summary?.score_percentage);
	if (!Number.isNaN(scorePercentage) && scorePercentage >= 0) {
		return clampPercentage(scorePercentage);
	}

	const score = Number(summary?.score);
	if (!Number.isNaN(score) && score >= 0) {
		if (score <= 1) return clampPercentage(score * 100);
		return clampPercentage(score);
	}

	const correct = Number(summary?.correct_count ?? summary?.correct_answers ?? 0);
	const total = Number(summary?.total_questions ?? summary?.number_of_questions ?? 0);
	if (total > 0) {
		return clampPercentage((correct / total) * 100);
	}

	return 0;
}

function formatSeconds(seconds) {
	const value = Number(seconds);
	if (Number.isNaN(value) || value < 0) return '00:00:00';

	const hours = Math.floor(value / 3600);
	const minutes = Math.floor((value % 3600) / 60);
	const secs = Math.floor(value % 60);

	return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

function formatSpanishDate(dateInput) {
	if (!dateInput) return '';
	const parsed = new Date(dateInput);
	if (Number.isNaN(parsed.getTime())) return '';

	return parsed.toLocaleDateString('es-MX', {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
	});
}

function MetricCard({ icon, label, value, subValue }) {
	return (
		<Box
			sx={{
				px: { xs: 0.25, md: 0.8 },
				py: { xs: 0.25, md: 0 },
				display: 'flex',
				alignItems: 'center',
				gap: { xs: 0.45, md: 1 },
				width: 'auto',
				minWidth: { md: 210 },
				flexShrink: 0,
			}}
		>
			<Box
				sx={{
					width: { xs: 28, md: 40 },
					height: { xs: 28, md: 40 },
					borderRadius: { xs: '8px', md: '12px' },
					border: '1px solid #d1d5db',
					color: '#334155',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					backgroundColor: '#ffffff',
					flexShrink: 0,
				}}
			>
				{icon}
			</Box>

			<Box sx={{ minWidth: 0 }}>
				<Typography sx={{ fontSize: { xs: 9, md: 11 }, color: '#475569', lineHeight: 1.05, fontWeight: 500 }}>{label}</Typography>
				<Typography sx={{ fontSize: { xs: 16, md: 22 }, color: '#0f172a', fontWeight: 700, lineHeight: 1.02, mt: 0.05 }}>{value}</Typography>
				{subValue ? <Typography sx={{ fontSize: { xs: 9, md: 11 }, color: '#64748b', mt: 0.05 }}>{subValue}</Typography> : null}
			</Box>
		</Box>
	);
}

function ScoreCard({ scorePercent, correctCount, totalQuestions }) {
	return (
		<Box
			sx={{
				px: { xs: 0.25, md: 0.8 },
				py: { xs: 0.25, md: 0 },
				display: 'flex',
				alignItems: 'center',
				gap: { xs: 0.45, md: 1.1 },
				width: 'auto',
				minWidth: { md: 250 },
				flexShrink: 0,
			}}
		>
			<Box sx={{ position: 'relative', width: { xs: 54, md: 70 }, height: { xs: 54, md: 70 }, flexShrink: 0 }}>
				<CircularProgress
					variant="determinate"
					value={100}
					size={54}
					thickness={6}
					sx={{ color: '#dbeafe', position: 'absolute', left: 0, top: 0, zIndex: 0, display: { xs: 'block', md: 'none' } }}
				/>
				<CircularProgress
					variant="determinate"
					value={100}
					size={70}
					thickness={6}
					sx={{ color: '#dbeafe', position: 'absolute', left: 0, top: 0, zIndex: 0, display: { xs: 'none', md: 'block' } }}
				/>
				<CircularProgress
					variant="determinate"
					value={scorePercent}
					size={54}
					thickness={6}
					sx={{ color: '#2563eb', position: 'absolute', left: 0, top: 0, zIndex: 1, display: { xs: 'block', md: 'none' } }}
				/>
				<CircularProgress
					variant="determinate"
					value={scorePercent}
					size={70}
					thickness={6}
					sx={{ color: '#2563eb', position: 'absolute', left: 0, top: 0, zIndex: 1, display: { xs: 'none', md: 'block' } }}
				/>
				<Box
					sx={{
						position: 'absolute',
						inset: 0,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<Typography sx={{ fontSize: { xs: 15, md: 20 }, fontWeight: 700, color: '#0f172a', lineHeight: 1 }}>{scorePercent}%</Typography>
				</Box>
			</Box>

			<Box sx={{ minWidth: 0 }}>
				<Typography sx={{ fontSize: { xs: 9, md: 11 }, color: '#475569', lineHeight: 1.05, fontWeight: 500 }}>Puntuacion final</Typography>
				<Typography sx={{ fontSize: { xs: 16, md: 22 }, color: '#0f172a', fontWeight: 700, lineHeight: 1.02, mt: 0.05 }}>
					{correctCount} / {totalQuestions}
				</Typography>
				<Typography sx={{ fontSize: { xs: 9, md: 11 }, color: '#64748b', mt: 0.05 }}>Aciertos</Typography>
			</Box>
		</Box>
	);
}

export default function HeaderResultadosPreguntas({
	resultsPayload,
	title = 'Revision de preguntas',
	subtitle,
	onBack,
	backLabel = 'Volver a resultados',
}) {
	const summary = resultsPayload?.summary || {};
	const examTitle = summary?.title || 'Simulador ENARM';
	const headerDate = formatSpanishDate(summary?.completed_at || summary?.started_at);
	const resolvedSubtitle = subtitle ?? `${examTitle}${headerDate ? ` - ${headerDate}` : ''}`;

	const correctCount = Number(summary?.correct_count ?? summary?.correct_answers ?? 0);
	const totalQuestions = Number(summary?.total_questions ?? summary?.number_of_questions ?? 0);
	const usedSeconds = Number(summary?.time_used_seconds ?? 0);
	const expectedSeconds = Number(summary?.duration_minutes ?? 0) > 0 ? Number(summary.duration_minutes) * 60 : 0;
	const scorePercent = normalizeScorePercent(summary);

	return (
		<Box
			sx={{
				borderBottom: '1px solid #e5e7eb',
				px: { xs: 1, md: 0 },
				py: { xs: 1.25, md: 1.5 },
				backgroundColor: '#ffffff',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					gap: { xs: 1.2, md: 2.5 },
					alignItems: { xs: 'flex-start', lg: 'center' },
					justifyContent: 'space-between',
					flexDirection: { xs: 'column', lg: 'row' },
				}}
			>
				<Box sx={{ flex: { lg: 1.2 }, minWidth: 0 }}>
					<Typography sx={{ fontSize: { xs: 20, md: 22 }, lineHeight: 1.08, color: '#0f172a', fontWeight: 700 }}>
						{title}
					</Typography>
					<Typography sx={{ mt: 0.45, fontSize: { xs: 14, md: 16 }, color: '#64748b' }}>
						{resolvedSubtitle}
					</Typography>
				</Box>

				<Box
					sx={{
						flex: { lg: 1 },
						display: 'flex',
						alignItems: 'center',
						flexDirection: 'row',
						gap: { xs: 1, md: 1.6 },
						flexWrap: 'nowrap',
						width: '100%',
						justifyContent: 'flex-start',
						overflowX: { xs: 'auto', md: 'visible' },
						pb: { xs: 0.25, md: 0 },
						'&::-webkit-scrollbar': {
							display: 'none',
						},
						scrollbarWidth: 'none',
					}}
				>
					<MetricCard
						icon={<Clock3 size={14} />}
						label="Tiempo utilizado"
						value={formatSeconds(usedSeconds)}
						subValue={`de ${formatSeconds(expectedSeconds)}`}
					/>

					<ScoreCard scorePercent={scorePercent} correctCount={correctCount} totalQuestions={totalQuestions} />

					{onBack ? (
						<Button
							variant="outlined"
							onClick={onBack}
							startIcon={<ArrowLeft size={18} />}
							sx={{
								ml: 'auto',
								alignSelf: 'center',
								textTransform: 'none',
								borderColor: '#93c5fd',
								color: '#2563eb',
								borderRadius: '10px',
								height: 46,
								minWidth: { xs: 42, sm: 'auto' },
								px: { xs: 1.1, sm: 2.4 },
								fontSize: 12,
								fontWeight: 600,
								whiteSpace: 'nowrap',
								flexShrink: 0,
								'& .MuiButton-startIcon': {
									mr: { xs: 0, sm: 1 },
									ml: 0,
								},
								'&:hover': {
									borderColor: '#60a5fa',
									backgroundColor: '#eff6ff',
								},
							}}
							aria-label={backLabel}
						>
							<Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
								{backLabel}
							</Box>
						</Button>
					) : null}
				</Box>
			</Box>
		</Box>
	);
}

